/**
 * Created by Ning Jiang on 2022-02-18.
 */

import {APIEventSystem} from "@nolimitcity/slot-launcher/bin/interfaces/APIEventSystem";
import {gsap} from "gsap";
import {SerialSymbol} from "../symbol/SerialSymbol";
import {IReelStateEventData, ReelState} from "../../core/reel/ReelState";
import {SlotGame} from "@nolimitcity/slot-game/bin/SlotGame";
import {SerialSymbolState} from "../symbol/SerialSymbolConfig";
import {SerialGameConfig} from "../SerialGameConfig";
import {Serial} from "../Serial";
import {MathHelper} from "../../core/utils/MathHelper";
import {APISetting} from "@nolimitcity/slot-launcher/bin/interfaces/APISettingsSystem";
import {EnhancerReelView} from "./EnhancerReelView";
import {EnhancerWXNSymbol} from "../symbol/EnhancerWXNSymbol";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {XWaysSymbol} from "../symbol/XWaysSymbol";
import {SingleWildSymbol} from "../symbol/SingleWildSymbol";
import {InfectiousSymbol} from "../symbol/InfectiousSymbol";
import {EmptySymbol} from "../symbol/EmptySymbol";
import {ReelPositionController} from "./ReelPositionController";
import {Logger} from "@nolimitcity/slot-launcher/bin/utils/Logger";
import {HiddenEnhancerReelView} from "./HiddenEnhancerReelView";

export class EnhancerReelController {

    public readonly events:APIEventSystem;

    public reelId:number;
    public positionId:number;
    public reelView:EnhancerReelView;
    public isQuickStop:boolean;
    public isOpened:boolean;

    private _symsNumTotal:number;
    private _currentReelSet:string[]; //The reelSet currently in use, it might be different than the actually reelSet because its made of many different parts.
    private _currentFullReelSet:string[]; // The full actually reelSet that the most part of the _currentReelSet is make of.
    private _stopCounter:number = 0;

    private _normalStartStepDuration:number = 0.15;
    private _fastStartStepDuration:number = 0.1;
    private _startEase:string = `circ.in`;
    private _defaultSpinStepDuration:number = 0.04;
    private _normalStopStepDuration:number = 0.15;
    private _fastStopStepDuration:number = 0.1;
    private _normalStopEase:string = `rough({ template: none.inOut, strength: 5, points: 20, taper: 'in', randomize: false, clamp: false})`;
    private _fastStopEase:string = this._normalStopEase;

    private _reelState:ReelState;
    get reelState():ReelState {
        return this._reelState;
    }

    set reelState(value:ReelState) {
        if(value === this._reelState) {
            return;
        }

        const oldState = this._reelState;
        this._reelState = value;
        // Logger.logDev(`Enhancer Reel ${this.reelId} ${this.positionId} state change from ${oldState} to ${this._reelState}`);
        this.events.trigger("ReelStateChange", <IReelStateEventData>{
            reelId : this.reelId,
            positionId : this.positionId,
            newState : this._reelState,
            oldState : oldState
        });
    }

    constructor(reelId:number, positionId:number, isOpened:boolean) {
        this.events = SlotGame.apiPlugin.eventSystemFactory.create();
        this.events.on("ReelStateChange", (data:IReelStateEventData) => this.events.trigger(data.newState, data)); //To prevent event system from saving once callbacks if no on callbacks is set.

        this.reelId = reelId;
        this.positionId = positionId;
        this.reelState = ReelState.IDLE;
        this._symsNumTotal = 1;
        this.reelView = (positionId === 0 || positionId === 6)?
            new EnhancerReelView(reelId, positionId, this.getViewInitHeight(isOpened)):
            new HiddenEnhancerReelView(reelId, positionId, this.getViewInitHeight(isOpened));
        this.initSymbols();
        this.setOpen(isOpened, true);
    }

    protected getViewInitHeight(isOpened:boolean):number {
        return ReelPositionController.DEFAULT_SYMBOL_HEIGHT * 0.5;
    }

    public setOpen(value:boolean, init:boolean = false):boolean {
        Serial.ways.updateWays(this.reelId, this.positionId, value ? 1 : 0, !init);
        if(this.isOpened === value) {
            return false;
        }

        this.isOpened = value;
        this.reelView.setOpened(value);
        return true;
    }

    private initSymbols():void {
        this._currentFullReelSet = SerialGameConfig.getReelSet("ENHANCER_REELSET", this.reelId);
        this._currentReelSet = this.makeUpStopReelSet([Serial.model.parsedInitData.fullReels[this.reelId][this.positionId]]);

        for(let i:number = 0; i < this._symsNumTotal; i++) {
            this.addNextNewSymbol(SerialSymbolState.NORMAL);
        }
        this.reelView.initSymbolsPosition(0);
    }

    // only for spin here
    private makeUpStopReelSet(stopReelSet:string[]):string[] {
        const randomPosition:number = this.randomizeReelSetPosition(this._currentFullReelSet);
        return this._currentFullReelSet.slice(0, randomPosition + 1).concat(stopReelSet);
    }

    // Get a position where the symbol is not part of a stacked symbol unless it's the bottom of the stacked symbol.
    private randomizeReelSetPosition(reelSet:string[]):number {
        return MathHelper.randomNumberInRange(4, reelSet.length - 1); // Start on 4 to make sure there is enough number included.
    }

    private addNextNewSymbol(state:SerialSymbolState):SerialSymbol {
        let symName:string;
        do {
            if(this._currentReelSet.length == 0) {
                if(this._currentFullReelSet == null) {
                    debugger;
                    throw new Error("ReelController.createNextSymbol():This is a bug that Ning is trying to reproduce!");
                }
                this._currentReelSet = this._currentFullReelSet.concat();
            }
            symName = this._currentReelSet.pop()!;
        } while(symName.includes("WXN") && this.specialSymbolsLockedInReel());

        let symbol:SerialSymbol = this.createSymbol(symName, this.reelId, state);
        this.reelView.addSymbol(symbol);
        this.reelView.allSymbols.unshift(symbol);

        const previousSymbol = this.reelView.allSymbols[1];
        if(previousSymbol) {
            symbol.y = previousSymbol.y - previousSymbol.symbolHeight * 0.5 - symbol.bottomPadding - symbol.symbolHeight * 0.5;
        }

        return symbol;
    }

    private specialSymbolsLockedInReel():boolean {
        if(!Serial.model.data.mode.includes(GameMode.RESPIN)) {
            return false;
        }

        return Serial.reels[this.reelId].overlaySymbols.some(sym => {
            return sym != null && (sym.symbolName.includes("X") || sym.symbolName.includes("S"));
        });
    }

    public createSymbol(symName:string, reelId:number, state:SerialSymbolState):SerialSymbol {
        if(symName == null || symName.length === 0) {
            return new EmptySymbol(reelId, ReelPositionController.DEFAULT_SYMBOL_HEIGHT * 0.5, state);
        }
        if(symName.includes(`WXN`)) {
            return new EnhancerWXNSymbol(symName, this.reelId, state);
        }
        if(symName.includes(`X`)) {
            return new XWaysSymbol(symName, this.reelId, ReelPositionController.DEFAULT_SYMBOL_HEIGHT * 0.5, state);
        }
        if(symName.includes(`W`)) {
            return new SingleWildSymbol(symName, this.reelId, ReelPositionController.DEFAULT_SYMBOL_HEIGHT * 0.5, state);
        }
        if(symName.includes(`:`)) {
            return new InfectiousSymbol(symName, this.reelId, ReelPositionController.DEFAULT_SYMBOL_HEIGHT * 0.5, state);
        }
        switch(symName) {
            default:
                return new SerialSymbol(symName, this.reelId, ReelPositionController.DEFAULT_SYMBOL_HEIGHT * 0.5, state);
        }
    }

    private removeBottomSymbol():void {
        const removeSymbol = this.reelView.allSymbols.pop()!;
        this.reelView.removeSymbol(removeSymbol);
        removeSymbol.onRemove();
    }

    private setNextState(reelState:ReelState, duration?:number, ease?:string):void {
        this.reelView.nextSpinStepConfig = {
            reelState : reelState,
            duration : duration,
            ease : ease
        }
    }

    public startSpin(active:boolean, delay:number):Promise<any> {
        this._stopCounter = 0;
        this.isQuickStop = false;
        this.reelView.wantBounce = false;

        if(!active) {
            this.reelState = ReelState.INACTIVE;
            return Promise.resolve();
        } else {
            Serial.reelStopSoundController.spinStates[this.reelId][this.positionId] = true;
            this.setNextState(
                ReelState.START,
                Serial.apiPlugin.settings.get(APISetting.FAST_SPIN) ? this._fastStartStepDuration : this._normalStartStepDuration,
                this._startEase
            );
            const tl = gsap.timeline({
                onComplete : () => {
                    if(active) {
                        this.setNextState(ReelState.SPIN, this._defaultSpinStepDuration, 'none');
                        this.spinStep();
                    }
                }
            });
            tl.add(this.reelView.step(this.addNextNewSymbol(SerialSymbolState.SPIN), () => this.removeBottomSymbol()), delay);
            return new Promise<any>(res => {
                this.events.once(ReelState.SPIN, async (data:IReelStateEventData) => {
                    res(undefined);
                });
            });
        }
    }

    public spinStep():gsap.core.Timeline {
        const tl = gsap.timeline({
            onComplete : () => {
                if(this.reelState === ReelState.SPIN) {
                    this.spinStep();
                    return;
                }
                if(this.reelState === ReelState.STOPPING) {
                    if(this._stopCounter > 1) {
                        this._stopCounter--;
                        this.spinStep();
                        return;
                    }

                    this._stopCounter = 0;
                    this.stopStep();
                }
            }
        });
        tl.add(this.reelView.step(this.addNextNewSymbol(SerialSymbolState.SPIN), () => this.removeBottomSymbol()));
        return tl;
    }

    public stopStep():gsap.core.Timeline {
        // Logger.logDev(`enhancer reel ${this.reelId} ${this.positionId} stop step started!`);
        this.setNextState(
            ReelState.STOPPING,
            (Serial.apiPlugin.settings.get(APISetting.FAST_SPIN) || this.isQuickStop) ? this._fastStopStepDuration : this._normalStopStepDuration,
            (Serial.apiPlugin.settings.get(APISetting.FAST_SPIN) || this.isQuickStop) ? this._fastStopEase : this._normalStopEase
        );
        const tl = gsap.timeline({
            onStart : () => {this.reelView.wantBounce = true},
            onComplete : () => {
                // Logger.logDev(`enhancer reel ${this.reelId} ${this.positionId} stop step completed!`);
                this.reelState = ReelState.STOPPED;
            }
        });
        tl.add(this.reelView.step(this.addNextNewSymbol(SerialSymbolState.NORMAL), () => this.removeBottomSymbol()));
        this.reelView.allSymbols.forEach((symbol) => {
            tl.add(() => {
                if(!symbol.transform) {
                    return;
                }
                symbol.getStateAnimation(symbol.getLandingState(),
                    {duration : this.reelView.currentStepDuration, alpha : 1},
                    {duration : this.reelView.currentStepDuration, alpha : 0})
            }, 0);
        });
        return tl;
    }

    public hasNearWin():boolean {
        return false;
        // TODO: return Serial.model.parsedGameData.nearWinBigWinReels[this.reelId] || Serial.model.parsedGameData.nearWinReels[this.reelId];
    }

    public tryStop(quickStop:boolean):void {
        // Logger.logDev(`enhancer reel ${this.reelId} ${this.positionId} try to stop.`);
        this.isQuickStop = quickStop;

        if(this.reelState === ReelState.INACTIVE) {
            Logger.logDev(`enhancer reel ${this.reelId} ${this.positionId} is inactive, stopped.`);
            this.reelState = ReelState.STOPPED;
            return;
        }

        if(this.reelState === ReelState.STOPPING) {
            Logger.logDev(`enhancer reel ${this.reelId} ${this.positionId} is already stopping.`);
            return;
        }

        this.getReadyToStop();
    }

    private getReadyToStop():void {
        // Logger.logDev(`enhancer reel ${this.reelId} ${this.positionId} ready to stop.`);
        this._currentReelSet = this.makeUpStopReelSet([Serial.model.parsedGameData.fullReels[this.reelId][this.positionId]]);
        this._stopCounter = this._symsNumTotal;

        this.reelState = ReelState.STOPPING;
    }

    public getSymbol():SerialSymbol {
        return this.reelView.allSymbols[0];
    }

    public replaceSymbol(newSymbol:SerialSymbol):SerialSymbol {
        const oldSymbol = this.getSymbol();
        newSymbol.position.set(oldSymbol.x, oldSymbol.y);
        this.reelView.addSymbol(newSymbol);
        this.reelView.allSymbols[this.reelView.allSymbols.indexOf(oldSymbol)] = newSymbol;
        this.reelView.removeSymbol(oldSymbol);

        return oldSymbol;
    }
}